import { action, makeObservable, observable } from "mobx";
import ActionsApi, { ActionsApi as IActionsApi } from "../../../../../services/api/v2/actions/Actions.api";
import { FilterStoreAddon } from "../../../../../apps/grids/builder/directorBuilder/grids/base/addons/FilterAddon/FilterStoreAddon";
import { ColumnStateAddon } from "../../../../../apps/grids/builder/directorBuilder/grids/base/addons/ColumnStateAddon";
import { TextWrapperAddon } from "../../../../../apps/grids/builder/directorBuilder/grids/base/addons/TextWrapAddon";
import { EntityTypes, GridTypes } from "../../../../../enums";
import { IModalContextModel } from "../../../../../core/modal/IModalContextModel";
import ProgressIndicatorModel, {
  IProgressIndicatorModel
} from "../../../../../components/widgets/ProgressIndicator/ProgressIndicator_model";
import ToasterService, { IToasterService } from "../../../../../core/toaster/ToasterService";
import { ColDef, GridApi, IRowNode } from "ag-grid-community";
import { IGridUiAction } from "../../../../../apps/grids/builder/directorBuilder/grids/base/AppGridToolbarActions_view";
import { GridModalBuilder } from "../../../../../apps/grids/builder/directorBuilder/modals/GridModal_builder";
import { IGridToastService } from "../../../../../services/local/gridToastService/IGridToastService";
import { AppGridToolbarType } from "../../../../../apps/grids/builder/directorBuilder/types/AppGrid_types";
import { NavigateFunction } from "react-router-dom";
import PermissionsContext from "../../../../../contexts/permissions/PermissionsContext";
import { PermissionFields } from "../../../../../contexts/permissions/PermissionsTypes";
import GridToastService from "../../../../../services/local/gridToastService/GridToastService";
import { CommonColDefFieldNamesEnum } from "../../../../../apps/grids/builder/enums/AgGridColDefFieldNameEnum";
import ProjectTeamUserPermissionsApi, {
  ProjectTeamUserPermissionsApi as IProjectTeamUserPermissionsApi
} from "../../../../../services/api/v2/projectTeamUserPermissions/ProjectTeamUserPermissions.api";
import { AppGridState } from "../../../../../apps/grids/builder/directorBuilder/grids/base/AppGrid_view";
import { ActionsGridDashboardColumnBuilder } from "./ActionsDashboardGrid_config";
export class ActionsDashboardGridModel {
  organisationId: number;
  projectId: number;
  @observable isLoading: boolean = true;
  progressStatuses: FP.Generic.IKeyLabel[];
  actionsProvider: IActionsApi;
  authUser: FP.Entities.IUser;
  filterStoreAddon: FilterStoreAddon;
  columnStateAddon: ColumnStateAddon;
  textWrapAddon: TextWrapperAddon;
  @observable isFilterChanged: boolean;
  @observable isColumnStateChanged: boolean;
  userCanViewActions: boolean;
  userCanEditActions: boolean;
  type: GridTypes = GridTypes.ACTIONS_GRID;
  actionsGridDashboardColumnBuilder: ActionsGridDashboardColumnBuilder;
  modalService: IModalContextModel;
  httpProgress: IProgressIndicatorModel;
  @observable confirmationService: IModalContextModel;
  @observable selectedItems: number[] = [];
  toasterService: IToasterService;
  @observable isDuplicatingItems: boolean = false;
  gridApi: GridApi;
  @observable.ref gridActions: IGridUiAction[] = [];
  projectTeamUserPermissionsProvider: IProjectTeamUserPermissionsApi = ProjectTeamUserPermissionsApi;
  gridModalBuilder: GridModalBuilder;
  gridToastService: IGridToastService;
  @observable projectTeamMembers: any[] = null;
  gridColumns: ColDef<any, any>[];
  columnDefs: FP.Entities.IColumnDef[];
  urlSearchParams: URLSearchParams;
  gridToolbarType: AppGridToolbarType;
  @observable preSelectedItemIds: number[];
  gridType: EntityTypes | GridTypes;
  navigate: NavigateFunction;
  onFieldUpdate: () => Promise<void>;
  refreshDataHandler?: () => Promise<void>;

  constructor(
    organisationId: number,
    projectId: number,
    authUser: FP.Entities.IUser,
    columnDefs: FP.Entities.IColumnDef[],
    urlSearchParams: URLSearchParams,
    preSelectedItemIds: number[],
    gridType: EntityTypes | GridTypes,
    navigate: NavigateFunction,
    onFieldUpdate: () => Promise<void>,
    refreshDataHandler?: () => Promise<void>
  ) {
    makeObservable(this);
    this.urlSearchParams = urlSearchParams;
    this.gridType = gridType;
    this.columnDefs = columnDefs;
    this.actionsProvider = ActionsApi;
    this.userCanViewActions = PermissionsContext.canViewField(PermissionFields.ACTIONS, organisationId, projectId);
    this.userCanEditActions = PermissionsContext.canEditField(PermissionFields.ACTIONS, organisationId, projectId);
    this.organisationId = organisationId;
    this.projectId = projectId;
    this.authUser = authUser;
    this.toasterService = ToasterService;
    this.httpProgress = ProgressIndicatorModel;
    this.gridToastService = GridToastService;
    this.gridModalBuilder = new GridModalBuilder();
    this.selectedItems = preSelectedItemIds;
    this.preSelectedItemIds = preSelectedItemIds;
    this.navigate = navigate;
    this.onFieldUpdate = onFieldUpdate;
    this.refreshDataHandler = refreshDataHandler;
  }

  @action
  onMount = async () => {
    this.generateFn();
    this.setIsLoading(false);
  };

  @action
  generateFn = () => {
    this.generateGridConfig();
  };

  onUnmount = () => {};

  generateGridConfig = () => {
    this.actionsGridDashboardColumnBuilder = new ActionsGridDashboardColumnBuilder({
      canEdit: true,
      organisationId: this.organisationId,
      projectId: this.projectId,
      userCanViewActions: this.userCanViewActions,
      projectTeamMembers: this.projectTeamMembers,
      columns: this.columnDefs,
      onFieldUpdate: this.onFieldUpdate
    });
    this.gridColumns = this.actionsGridDashboardColumnBuilder.generateColumnDefs();
  };

  @action
  loadProjectTeamMembers = async () => {
    const res = await this.projectTeamUserPermissionsProvider.getAllUsersSimple(this.organisationId, this.projectId);
    if (!res || res.isError) return;
    this.setProjectTeamMembers(res.payload);
  };

  @action
  setProjectTeamMembers = projectTeamMembers => {
    this.projectTeamMembers = projectTeamMembers;
    this.gridApi.getColumnDef(CommonColDefFieldNamesEnum.Owner).cellEditorParams.options = projectTeamMembers;
  };

  setGridApi = e => {
    this.gridApi = e.api;
    if (
      !this.projectTeamMembers &&
      e.type === "gridReady" &&
      this.userCanEditActions &&
      this.gridToolbarType !== "link-modal"
    ) {
      this.loadProjectTeamMembers();
    }
  };

  @action
  setIsLoading = (isLoading: boolean) => {
    this.isLoading = isLoading;
  };

  @action
  setGridActions = (gridActions: any) => {
    this.gridActions = gridActions;
  };

  @action
  onGridStateUpdate = (gridState: AppGridState) => {
    this.gridApi = gridState.gridApi;
    this.selectedItems = gridState.selectedItems || [];
  };

  @action
  setSelectedNodes = (gridApi: GridApi) => {
    const nodesToSelect: IRowNode[] = [];
    gridApi.forEachNode(node => {
      if (!!this.preSelectedItemIds?.includes(node.data.id)) {
        nodesToSelect.push(node);
      }
    });
    gridApi.setNodesSelected({ nodes: nodesToSelect, newValue: true });
  };

  removeItems = async (itemIds: number[]) => {
    this.httpProgress.showOverlay();
    let res = await this.actionsProvider.deleteRange(this.organisationId, this.projectId, itemIds);
    this.httpProgress.hideOverlay();
    if (!res || res.isError) return;

    return res.payload;
  };
}
